import React, { Component, forwardRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { ClearIcon } from 'components/common/icons';
import CustomTooltip from 'components/common/CustomTooltip';

import styles from 'components/common/Form/Input/Input.module.scss';
import InputEventTypes from 'constants/InputTypeInEventTarget';
import KeyboardKeys from 'constants/KeyboardKeys';

class Input extends Component {
    static propTypes = {
        onEnter: PropTypes.func,
        onClick: PropTypes.func,
        onFocus: PropTypes.func,
        onChange: PropTypes.func,
        onChangeField: PropTypes.func,
        onClearField: PropTypes.func,
        onBlur: PropTypes.func,
        active: PropTypes.bool,
        label: PropTypes.string,
        type: PropTypes.string,
        name: PropTypes.string,
        placeholder: PropTypes.string,
        value: PropTypes.any,
        className: PropTypes.string,
        wrapperClassName: PropTypes.string,
        showBtn: PropTypes.bool,
        customBtn: PropTypes.func,
        readOnly: PropTypes.bool,
        maxLength: PropTypes.number,
        withLengthCounter: PropTypes.bool,
        rows: PropTypes.number,
        error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        icon: PropTypes.func,
        rightIcon: PropTypes.func,
        iconPadding: PropTypes.string,
        errorAbsolutePosition: PropTypes.bool,
        customTooltipText: PropTypes.string,
        withManyIcons: PropTypes.bool,
        autoComplete: PropTypes.string,
        firstCapitalized: PropTypes.bool,
        withoutClearBtn: PropTypes.bool,
        disabled: PropTypes.bool,
        readOnlyClassName: PropTypes.string,
        hidden: PropTypes.bool,
    };

    static defaultProps = {
        iconPadding: '0.63rem 3rem',
        customTooltipPosition: 'bottom',
        autoComplete: 'true',
    };

    constructor(props) {
        super(props);
        this.state = {
            isFocused: false,
        };

        this.inputRef = React.createRef();
    }

    componentDidMount() {
        if (this.props.withFocus) this.inputRef.focus();
    }

    onFocus = (e) => {
        const { onFocus } = this.props;

        this.setState({ isFocused: true });
        onFocus && onFocus(e);
    };

    onBlur = (e) => {
        const { onBlur } = this.props;

        this.setState({ isFocused: false });
        onBlur && onBlur(e);
    };

    onChange = (e) => {
        const { onChangeField, onChange, firstCapitalized } = this.props;

        if (onChangeField) {
            onChangeField(e);
        } else {
            if (e.nativeEvent.inputType === InputEventTypes.insertLineBreak) return;
            let value = e.currentTarget.value;
            if (firstCapitalized && value) {
                value = value[0].toUpperCase() + value.slice(1);
            }
            onChange && onChange(value);
        }
    };

    onClear = () => {
        const { name, onChangeField, onClearField, onChange } = this.props;
        if (this.inputRef.rows) {
            this.inputRef.rows = 1;
        }

        if (onClearField) {
            onClearField({ name });

            if (this.inputRef.focus) {
                this.inputRef.focus();
            }

            return;
        }

        if (onChangeField) {
            onChangeField({ target: { name, value: '' } });
        } else {
            onChange && onChange('');
        }
        if (this.inputRef.focus) {
            this.inputRef.focus();
        }
    };

    onKeyDown = (e) => {
        const { onEnter } = this.props;
        if (e.keyCode === KeyboardKeys.Enter && e.shiftKey) {
            e.preventDefault();
            this.addNewLineToTextArea(e);
        } else if (e.keyCode === KeyboardKeys.Enter && onEnter) {
            this.inputRef.rows = 1;
            onEnter();
        }
    };
    addNewLineToTextArea(e) {
        if (this.inputRef.rows < 3) {
            this.inputRef.rows += 1;
        }
        this.inputRef.value += '\n';
    }
    getLabelAdditionalClass() {
        const { active, value, error } = this.props;
        const { isFocused } = this.state;

        if (error) {
            return styles.errorLabel;
        }

        if (active || isFocused) {
            return styles.primaryLabel;
        }

        return '';
    }

    renderIcon() {
        const {
            customBtn,
            icon,
            showBtn,
            value,
            readOnly,
            withoutClearBtn,
            rightIcon,
            withManyIcons = false,
        } = this.props;

        const leftIcon = icon && icon({ className: styles.icon });
        const rightIconRender = rightIcon && rightIcon({ className: styles.rightIcon });
        const showClearButton = value?.length > 0 && !withoutClearBtn;

        return (
            <>
                {leftIcon}
                {showClearButton && (
                    <ClearIcon
                        className={cn(styles.btn, { [styles.textareaBtn]: this.props.textarea })}
                        onClick={this.onClear}
                    />
                )}
                {!showClearButton && rightIconRender}
            </>
        );
    }

    render() {
        const {
            textarea,
            label,
            type,
            name,
            placeholder,
            value,
            className,
            wrapperClassName,
            readOnly,
            error,
            rows,
            maxLength,
            withLengthCounter,
            onClick,
            icon,
            iconPadding,
            errorAbsolutePosition,
            note,
            containerRef,
            customTooltipText,
            customTooltipPosition,
            autoComplete,
            labelClassName,
            inputId,
            anchorId,
            disabled,
            autoFocus,
            register,
            required,
            isNoteAfterInput,
            inputClassName,
            readOnlyClassName,
            actionBtn = null,
            hidden = false,
        } = this.props;

        const showClearButton = value !== '' && !readOnly;
        const labelClasses = cn(styles.inputLabel, this.getLabelAdditionalClass());
        const wrapperClasses = cn(styles.inputWrapper, wrapperClassName, {
            [styles.disabled]: disabled,
        });

        const inputClasses = cn(styles.input, className, inputClassName, {
            [readOnlyClassName ? readOnlyClassName : styles.readOnly]: readOnly,
            [styles.textarea]: textarea,
            [styles.withClearIcon]: showClearButton,
            [styles.errorInput]: error,
            [styles.hidden]: hidden,
        });

        let inputProps = {
            readOnly,
            name,
            value: value === undefined ? '' : value,
            placeholder,
            maxLength,
            onClick,
            ref: (el) => {
                this.inputRef = el;
                if (this.props.outerRef) this.props.outerRef(el);
            },
            className: inputClasses,
            type: type || 'text',
            onChange: this.onChange,
            onFocus: this.onFocus,
            onBlur: this.onBlur,
            rows: textarea ? rows : undefined,
            onKeyDown: this.onKeyDown,
            autoComplete,
            id: `${inputId}_input`,
            autoFocus,
        };
        if (register) {
            delete inputProps.value;

            inputProps = { ...inputProps, ...register(name, { required: required }) };
        }

        return (
            <div className={wrapperClasses} ref={containerRef} id={anchorId}>
                {(label || withLengthCounter) && (
                    <div className={cn(styles.labelContainer, labelClassName)}>
                        <div className={styles.labelWithActionBtnWrapper}>
                            {label && <label className={labelClasses}>{label}</label>}
                            {actionBtn && actionBtn}
                        </div>
                        {withLengthCounter && (
                            <span className={styles.lengthCounter}>
                                {value ? value.length : 0}
                                {'/'}
                                {maxLength}
                            </span>
                        )}
                    </div>
                )}
                {note && !isNoteAfterInput && <div className={styles.noteContainer}>{note}</div>}
                {customTooltipText && customTooltipPosition === 'top' && (
                    <div className={styles.customTooltipContainer}>
                        <CustomTooltip
                            text={customTooltipText}
                            displayed={this.state.toolTipHovered && !error && !value}
                            position={customTooltipPosition}
                        />
                    </div>
                )}
                <div
                    className={styles.inputWithBtn}
                    onMouseEnter={() => this.setState({ toolTipHovered: true })}
                    onMouseLeave={() => this.setState({ toolTipHovered: false })}
                >
                    {textarea ? (
                        <textarea {...inputProps} />
                    ) : (
                        <input style={icon && { padding: iconPadding }} {...inputProps} />
                    )}

                    {this.renderIcon()}
                </div>
                {note && isNoteAfterInput && <div className={styles.afterInputNoteContainer}>{note}</div>}
                {customTooltipText && customTooltipPosition === 'bottom' && (
                    <div className={styles.customTooltipContainer}>
                        <CustomTooltip
                            text={customTooltipText}
                            displayed={this.state.toolTipHovered && !error && !value}
                        />
                    </div>
                )}
                {error && <div className={cn(styles.error, errorAbsolutePosition && styles.absolute)}>{error}</div>}
            </div>
        );
    }
}

export default forwardRef((props, ref) => <Input containerRef={ref} {...props} />);
